import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { ProgressDashboardRawData, ProgressDashboardParsedData, CurrentMonthAdvancedTrendResponse } from "../../../interfaces/reports/ProgressDashboardReport.interface";
import { MoneyDataItem, PercentDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import AuthProvider from "../../../providers/AuthProvider";

interface ProgressDashboardTableReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
}

// Days in today's month
const DAYS_IN_TODAY_MONTH = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
const TODAY_DAY = new Date().getDate();

// Moved data processing to a separate function
const processRawData = (
    rawData: ProgressDashboardRawData[],
    trend: number
): ProgressDashboardParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    // Parse the raw data into controlled and formattable types
    return rawData.map((item) => {
        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            parentClientClassCode: item.parentClientClassCode || null,
            category: item.category ? item.category == "" ? "N/A" : item.category : "N/A",
            categorySortOrder: item.categorySortOrder ?? null,
            mtdForecastSales: new MoneyDataItem(item.forecastSalesTotal && trend > 0 ? item.forecastSalesTotal * trend : 0),
            mtdActualSales: new MoneyDataItem(item.mtdActualSales),
            forecastSalesTotal: new MoneyDataItem(item.forecastSalesTotal),
            trendSalesTotal: new MoneyDataItem(item.mtdActualSales && trend > 0 ? item.mtdActualSales / trend : 0),
            mtdActualLastYearSales: new MoneyDataItem(item.totalSalesLastYear && trend > 0 ? item.totalSalesLastYear * trend : 0),
            totalSalesLastYear: new MoneyDataItem(item.totalSalesLastYear),
            percentVsForecast: new PercentDataItem(item.percentVsForecast),
            percentVsLastYear: new PercentDataItem(item.percentVsLastYear)
        }
    });
};

const reapplyPercentCalcs = (data: ProgressDashboardParsedData[]) => {
    // Reapply percent calculations based on the hierarchy
    return data.map(item => {
        const percentVsForecast = item.mtdActualSales && item.mtdForecastSales && item.mtdForecastSales.value && typeof item.mtdForecastSales.value === 'number' && typeof item.mtdActualSales.value === 'number'
            ? new PercentDataItem((item.mtdActualSales.value / item.mtdForecastSales.value) * 100 - 100)
            : null; 
        const percentVsLastYear = item.mtdActualSales && item.mtdActualLastYearSales && item.mtdActualLastYearSales.value && typeof item.mtdActualLastYearSales.value === 'number' && typeof item.mtdActualSales.value === 'number'
            ? new PercentDataItem((item.mtdActualSales.value / item.mtdActualLastYearSales.value) * 100 - 100)
            : null;

        return {
            ...item,
            percentVsForecast,
            percentVsLastYear
        };
    });
}

const ProgressDashboardTableReport = ({ clientId, epochMonth }: ProgressDashboardTableReportProps) => {
    const globalTip = TipContext.useMessage();
    const auth = AuthProvider.useAuth() as any;
    const isAdmin = Boolean(auth?.checkPermission?.('role-create'));
    const dataRef = useRef<ProgressDashboardRawData[]>([]);
    const parentDataRef = useRef<ProgressDashboardRawData[]>([]);
    const [parsedData, setParsedData] = useState<ProgressDashboardParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<ProgressDashboardParsedData[]>([]);
    const [sortedData, setSortedData] = useState<ProgressDashboardParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["locationCode", "classCode"]);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    // Trend state
    const [baseTrend, setBaseTrend] = useState<number>(TODAY_DAY / DAYS_IN_TODAY_MONTH);
    const [advancedTrendActuals, setAdvancedTrendActuals] = useState<number | null>(null);
    const [advancedTrendPlan, setAdvancedTrendPlan] = useState<number | null>(null);
    const [advancedTrendAvailable, setAdvancedTrendAvailable] = useState(false);
    const [trendMode, setTrendMode] = useState<'standard' | 'advanced'>('standard');
    const [advancedTrendBasis, setAdvancedTrendBasis] = useState<'actuals' | 'plan'>('actuals');
    const [customTrend, setCustomTrend] = useState<number>(TODAY_DAY / DAYS_IN_TODAY_MONTH);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const columns = [
        ...hierarchyColumns,
        { name: "MTD Forecast", sortable: true, customClassName: "px-4 py-2" },
        { name: "MTD Actual", sortable: true, customClassName: "px-4 py-2" },
        { name: "Forecast Total", sortable: true, customClassName: "px-4 py-2" },
        { name: "Trend Total", sortable: true, customClassName: "px-4 py-2" },
        { name: "MTD Actual LY", sortable: true, customClassName: "px-4 py-2" },
        { name: "Total LY", sortable: true, customClassName: "px-4 py-2" },
        { name: "% VS Forecast", sortable: true, customClassName: "px-4 py-2" },
        { name: "% VS LY", sortable: true, customClassName: "px-4 py-2" },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setDataLoaded(false);
            setAdvancedTrendActuals(null);
            setAdvancedTrendPlan(null);
            setAdvancedTrendAvailable(false);
            setTrendMode('standard');
            setAdvancedTrendBasis('actuals');
            ReportService.getProgressDashboardReportData({ clientId, epochMonth }).then((response) => {
                if (response) {
                    // Store raw data in refs
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    // Extract custom trend from parentData (max of trend)
                    let maxTrend = 0;
                    if (response.data && response.data.length > 0) {
                        response.data.forEach((item: ProgressDashboardRawData) => {
                            if (item.trend && item.trend > maxTrend) {
                                maxTrend = item.trend;
                            }
                        });
                    }
                    const trend = maxTrend > 0 ? maxTrend : TODAY_DAY / DAYS_IN_TODAY_MONTH;
                    setBaseTrend(trend);
                    setCustomTrend(trend);

                    // Process both child and parent data
                    const processed = processRawData(response.data ?? [], trend);
                    const processedParent = processRawData(response.parentLevelData ?? [], trend);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    let sorted = ReportService.groupByHierarchy(processedParent, hierarchy ?? ["locationCode", "classCode"]);
                    sorted = reapplyPercentCalcs(sorted);
                    setSortedData(sorted);
                    setDataLoaded(true);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                    setDataLoaded(false);
                }
                setLoading(false);
            }).catch((error) => {
                console.error("Error fetching data:", error);
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth]);

    useEffect(() => {
        if (!dataLoaded || !isAdmin) {
            setAdvancedTrendActuals(null);
            setAdvancedTrendPlan(null);
            setAdvancedTrendAvailable(false);
            return;
        }

        let cancelled = false;

        const fetchAdvancedTrend = async () => {
            try {
                const response = await ReportService.getCurrentMonthAdvancedTrendData({ clientId, epochMonth });
                if (cancelled) {
                    return;
                }

                const responseData = (response as { data?: CurrentMonthAdvancedTrendResponse } | undefined)?.data;
                const payload = responseData && !Array.isArray(responseData)
                    ? responseData
                    : (response as CurrentMonthAdvancedTrendResponse | undefined);
                const nextAdvancedTrendActuals = payload?.advancedTrendActuals ?? payload?.advancedTrend ?? null;
                const nextAdvancedTrendPlan = payload?.advancedTrendPlan ?? null;
                const hasActualsTrend = typeof nextAdvancedTrendActuals === 'number' && Number.isFinite(nextAdvancedTrendActuals) && nextAdvancedTrendActuals > 0;
                const hasPlanTrend = typeof nextAdvancedTrendPlan === 'number' && Number.isFinite(nextAdvancedTrendPlan) && nextAdvancedTrendPlan > 0;
                setAdvancedTrendActuals(hasActualsTrend ? nextAdvancedTrendActuals : null);
                setAdvancedTrendPlan(hasPlanTrend ? nextAdvancedTrendPlan : null);
                setAdvancedTrendAvailable(hasActualsTrend || hasPlanTrend);
            } catch (error) {
                console.error("Error fetching advanced trend data:", error);
                setAdvancedTrendAvailable(false);
                if (!cancelled && globalTip) {
                    globalTip.show("Failed to fetch advanced trend data");
                }
            }
        };

        fetchAdvancedTrend();

        return () => {
            cancelled = true;
        };
    }, [clientId, epochMonth, dataLoaded, globalTip, isAdmin]);

    useEffect(() => {
        // When custom trend changes, reprocess the raw data
        let parsedData = processRawData(dataRef.current, customTrend);
        let parentParsedData = processRawData(parentDataRef.current, customTrend);
        setParsedData(parsedData);
        setParentParsedData(parentParsedData);
    }, [customTrend]);

    useEffect(() => {
        if (trendMode === 'advanced') {
            const selectedAdvancedTrend = advancedTrendBasis === 'plan' ? advancedTrendPlan : advancedTrendActuals;
            if (selectedAdvancedTrend) {
                setCustomTrend(selectedAdvancedTrend);
                return;
            }
        }

        setCustomTrend(baseTrend);
    }, [trendMode, advancedTrendActuals, advancedTrendPlan, advancedTrendBasis, baseTrend]);

    useEffect(() => {
        // Use parent or child data based on hierarchy
        const useParent = !hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyPercentCalcs(newSortedData);
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter]);

    const tableData = useMemo(() => {
        return sortedData.map((item, index) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);
            return [
                ...labelRows,
                item.mtdForecastSales ? item.mtdForecastSales.formatted : "",
                item.mtdActualSales ? item.mtdActualSales.formatted : "",
                item.forecastSalesTotal ? item.forecastSalesTotal.formatted : "",
                item.trendSalesTotal ? item.trendSalesTotal.formatted : "",
                item.mtdActualLastYearSales ? item.mtdActualLastYearSales.formatted : "",
                item.totalSalesLastYear ? item.totalSalesLastYear.formatted : "",
                item.percentVsForecast ? item.percentVsForecast.formatted : "",
                item.percentVsLastYear ? item.percentVsLastYear.formatted : ""
            ]
        });
    }, [sortedData, hierarchy]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <div className="w-full mb-4 flex flex-col gap-3 items-start justify-between px-4">
                <div className="text-left">
                    <h2 className="text-2xl font-bold">Progress Dashboard</h2>
                    <p className="text-sm text-amber-700 mt-1">
                        Advanced Trend is beta and projects month-end sales using plan and history values.
                    </p>
                </div>
            </div>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="w-full h-full">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title="Progress Dashboard"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Progress_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}

                        customTrend={customTrend}
                        setCustomTrend={setCustomTrend}
                        trendLabel="Trend Value"

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        filteredCategories={categoryFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        setFilteredCategories={setCategoryFilter}
                        extraActionButtons={isAdmin && advancedTrendAvailable ? (
                            <div className="flex items-center gap-2">
                                {trendMode === 'advanced' && advancedTrendPlan && advancedTrendActuals && (
                                    <button
                                        type="button"
                                        className="h-8 rounded-lg border border-amber-300 bg-amber-50 px-3 text-[12px] font-medium text-amber-900 shadow-sm transition hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-200"
                                        onClick={() => setAdvancedTrendBasis((current) => current === 'actuals' ? 'plan' : 'actuals')}
                                    >
                                        Basis: {advancedTrendBasis === 'actuals' ? 'Actuals' : 'Plan'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`h-8 px-4 text-[13px] font-semibold shadow-sm focus:outline-none focus:ring-2 transition rounded-lg ${trendMode === 'advanced'
                                        ? 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-200'
                                        : 'border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 focus:ring-amber-200'}`}
                                    onClick={() => setTrendMode((current) => current === 'advanced' ? 'standard' : 'advanced')}
                                >
                                    {trendMode === 'advanced' ? 'Use Standard Trend' : 'Use Advanced Trend (Beta)'}
                                </button>
                            </div>
                        ) : null}

                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = processRawData(dataRef.current, customTrend);
                            setParsedData(parsedData);
                        }}
                    />
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface ProgressDashboardTableReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const ProgressDashboardTableReportModal = ({ clientId, epochMonth, onClose }: ProgressDashboardTableReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <ProgressDashboardTableReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    ProgressDashboardTableReport,
    ProgressDashboardTableReportModal
}